import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Tooltip,
  InputAdornment
} from "@material-ui/core";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  MonetizationOn as MoneyIcon,
  DirectionsCar as CarIcon,
  Home as HomeIcon,
  Assessment as ChartIcon,
  Search as SearchIcon
} from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: "1.8rem",
    color: "#333",
  },
  headerSubtitle: {
    color: "#666",
    fontSize: "0.9rem",
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  metricsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  metricCard: {
    flex: 1,
    minWidth: 180,
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  metricValue: {
    fontSize: "1.6rem",
    fontWeight: 700,
  },
  metricLabel: {
    color: "#666",
    fontSize: "0.85rem",
  },
  filtersRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
  },
  filterControl: {
    minWidth: 160,
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  tableHeader: {
    backgroundColor: "#1a237e",
    "& th": {
      color: "#fff",
      fontWeight: 600,
      fontSize: "0.8rem",
    },
  },
  tableRow: {
    transition: "background-color 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: "#fff59d",
    },
  },
  chipAuto: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    fontWeight: 600,
  },
  chipImovel: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
  },
  commissionCell: {
    fontWeight: 700,
    color: "#2e7d32",
  },
  uploadDialog: {
    minWidth: 500,
  },
  uploadArea: {
    border: "2px dashed #ccc",
    borderRadius: 12,
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.3s",
    "&:hover": {
      borderColor: "#1a237e",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: "#999",
  },
  totalRow: {
    backgroundColor: "#f5f5f5",
    "& td": {
      fontWeight: 700,
      fontSize: "0.9rem",
    },
  },
}));

const ContemplatedLetters = ({ location }) => {
  const classes = useStyles();
  const routeType = location?.pathname.endsWith("/vehicles")
    ? "AUTO"
    : location?.pathname.endsWith("/properties")
      ? "IMÓVEIS"
      : "";
  const isScopedPage = Boolean(routeType);
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [commissionPercentage, setCommissionPercentage] = useState(3);
  const [newCommission, setNewCommission] = useState(3);
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState(routeType);
  const [filterAdmin, setFilterAdmin] = useState("");
  const [searchText, setSearchText] = useState("");

  const fetchLetters = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterAdmin) params.administrator = filterAdmin;
      const { data } = await api.get("/contemplated-letters", { params });
      setLetters(data);
    } catch (err) {
      toast.error("Erro ao carregar cartas contempladas");
    }
    setLoading(false);
  }, [filterType, filterAdmin]);

  useEffect(() => {
    setFilterType(routeType);
  }, [routeType]);

  useEffect(() => {
    fetchLetters();
  }, [fetchLetters]);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Selecione um arquivo PDF");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("commissionPercentage", commissionPercentage);
      const { data } = await api.post("/contemplated-letters/upload", formData);
      toast.success(data.message);
      setUploadOpen(false);
      setFile(null);
      fetchLetters();
    } catch (err) {
      const message = err?.response?.data?.error || "Erro ao importar PDF";
      toast.error(message);
    }
    setUploading(false);
  };

  const handleUpdateCommission = async () => {
    try {
      await api.put("/contemplated-letters/commission", { percentage: newCommission });
      toast.success("Comissões atualizadas com sucesso!");
      setCommissionOpen(false);
      fetchLetters();
    } catch (err) {
      toast.error("Erro ao atualizar comissões");
    }
  };

  const handleClearAll = async () => {
    if (window.confirm("Tem certeza que deseja remover TODAS as cartas contempladas? Esta ação não pode ser desfeita.")) {
      try {
        await api.delete("/contemplated-letters/clear");
        toast.success("Todas as cartas foram removidas");
        fetchLetters();
      } catch (err) {
        toast.error("Erro ao limpar cartas");
      }
    }
  };

  const handleSingleCommissionChange = async (letterId, newPercent) => {
    const percent = parseFloat(newPercent);
    if (isNaN(percent) || percent < 0) return;
    try {
      const { data } = await api.put(`/contemplated-letters/${letterId}/commission`, {
        commissionPercentage: percent
      });
      // Atualizar localmente sem recarregar tudo
      setLetters(prev => prev.map(l => 
        l.id === letterId 
          ? {
              ...l,
              commissionPercentage: data.commissionPercentage,
              commissionValue: data.commissionValue,
              totalEntryValue: data.totalEntryValue ?? (parseFloat(l.entryValue) + parseFloat(data.commissionValue || 0)),
              totalEntryPercentage: data.totalEntryPercentage ?? (
                parseFloat(l.credit || 0) > 0
                  ? ((parseFloat(l.entryValue || 0) + parseFloat(data.commissionValue || 0)) / parseFloat(l.credit)) * 100
                  : 0
              )
            }
          : l
      ));
    } catch (err) {
      toast.error("Erro ao atualizar comissão");
    }
  };

  // Métricas
  const totalLetters = letters.length;
  const totalAuto = letters.filter(l => ["AUTO", "MOTO", "VEÍCULO"].includes(l.type)).length;
  const totalImovel = letters.filter(l => l.type === "IMÓVEIS").length;
  const totalCredit = letters.reduce((sum, l) => sum + parseFloat(l.credit), 0);
  const totalCommission = letters.reduce((sum, l) => sum + parseFloat(l.commissionValue), 0);

  // Lista de administradoras únicas
  const administrators = [...new Set(letters.map(l => l.administrator).filter(Boolean))].sort();

  // Filtro por texto
  const filteredLetters = letters.filter(l => {
    if (!searchText) return true;
    return String(l.code || "").toLowerCase().includes(searchText.toLowerCase()) ||
           String(l.administrator || "").toLowerCase().includes(searchText.toLowerCase()) ||
           String(l.type || "").toLowerCase().includes(searchText.toLowerCase());
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <div>
          <Typography className={classes.headerTitle}>
            {filterType === "AUTO" ? "Cartas de Veículos" : filterType === "IMÓVEIS" ? "Cartas de Imóveis" : "Cartas Contempladas"}
          </Typography>
          <Typography className={classes.headerSubtitle}>
            Gerencie as cartas contempladas da Embracon e calcule comissões
          </Typography>
        </div>
        <div className={classes.headerActions}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<MoneyIcon />}
            onClick={() => setCommissionOpen(true)}
          >
            Comissão
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Importar PDF
          </Button>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchLetters}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Limpar tudo">
            <IconButton onClick={handleClearAll} color="secondary">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Métricas */}
      <div className={classes.metricsRow}>
        <Card className={classes.metricCard}>
          <CardContent>
            <Typography className={classes.metricLabel}>Total de Cartas</Typography>
            <Typography className={classes.metricValue}>{totalLetters}</Typography>
          </CardContent>
        </Card>
        <Card className={classes.metricCard}>
          <CardContent>
            <Typography className={classes.metricLabel}>
              <CarIcon style={{ fontSize: 16, verticalAlign: "middle" }} /> Veículos
            </Typography>
            <Typography className={classes.metricValue} style={{ color: "#1565c0" }}>
              {totalAuto}
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.metricCard}>
          <CardContent>
            <Typography className={classes.metricLabel}>
              <HomeIcon style={{ fontSize: 16, verticalAlign: "middle" }} /> Imóveis
            </Typography>
            <Typography className={classes.metricValue} style={{ color: "#2e7d32" }}>
              {totalImovel}
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.metricCard}>
          <CardContent>
            <Typography className={classes.metricLabel}>Crédito Total</Typography>
            <Typography className={classes.metricValue} style={{ fontSize: "1.2rem" }}>
              {formatCurrency(totalCredit)}
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.metricCard}>
          <CardContent>
            <Typography className={classes.metricLabel}>Entrada + Comissão (Total)</Typography>
            <Typography className={classes.metricValue} style={{ color: "#2e7d32" }}>
              {formatCurrency(letters.reduce((sum, l) => sum + parseFloat(l.totalEntryValue ?? (parseFloat(l.entryValue || 0) + parseFloat(l.commissionValue || 0))), 0))}
            </Typography>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className={classes.filtersRow}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Buscar..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        {!isScopedPage && (
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Tipo</InputLabel>
            <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Tipo">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="AUTO">Veículos</MenuItem>
              <MenuItem value="IMÓVEIS">Imóveis</MenuItem>
            </Select>
          </FormControl>
        )}
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Administradora</InputLabel>
          <Select value={filterAdmin} onChange={(e) => setFilterAdmin(e.target.value)} label="Administradora">
            <MenuItem value="">Todas</MenuItem>
            {administrators.map(adm => (
              <MenuItem key={adm} value={adm}>{adm}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      ) : filteredLetters.length === 0 ? (
        <div className={classes.emptyState}>
          <ChartIcon style={{ fontSize: 60, color: "#ccc" }} />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Nenhuma carta contemplada importada
          </Typography>
          <Typography variant="body2">
            Clique em "Importar PDF" para carregar as cartas da Embracon
          </Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell style={{ minWidth: 120 }}>Código da Carta</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Crédito</TableCell>
                <TableCell align="right">Entrada</TableCell>
                <TableCell align="center">Entrada %</TableCell>
                <TableCell align="center">Parcelas</TableCell>
                <TableCell>Administradora</TableCell>
                <TableCell align="right">Comissão %</TableCell>
                <TableCell align="right">Comissão R$</TableCell>
                <TableCell align="right">Entrada + Comissão (Total)</TableCell>
              <TableCell align="right">Nova % Ent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLetters.map((letter) => (
                <TableRow key={letter.id} className={classes.tableRow}>
                  <TableCell style={{ minWidth: 120 }}>
                    <strong style={{ color: "#1a237e", letterSpacing: 0.4 }}>{letter.code || "N/A"}</strong>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={letter.type}
                      className={["AUTO", "MOTO", "VEÍCULO"].includes(letter.type) ? classes.chipAuto : classes.chipImovel}
                    />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(letter.credit)}</TableCell>
                  <TableCell align="right">{formatCurrency(letter.entryValue)}</TableCell>
                  <TableCell align="center">{letter.entryPercentage}%</TableCell>
                  <TableCell align="center">{letter.installmentsText || (letter.installmentsCount + "x " + formatCurrency(letter.installmentValue))}</TableCell>
                  <TableCell>{letter.administrator}</TableCell>
                  <TableCell align="right" style={{ minWidth: 100 }}>
                    <TextField
                      size="small"
                      type="number"
                      variant="outlined"
                      value={letter.commissionPercentage}
                      onChange={(e) => {
                        const val = e.target.value;
                        setLetters(prev => prev.map(l => 
                          l.id === letter.id ? { ...l, commissionPercentage: val } : l
                        ));
                      }}
                      onBlur={(e) => handleSingleCommissionChange(letter.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        style: { fontSize: 13 }
                      }}
                      inputProps={{ min: 0, step: 0.5, style: { padding: '6px 8px', width: 50 } }}
                    />
                  </TableCell>
                  <TableCell align="right" className={classes.commissionCell}>
                    {formatCurrency(letter.commissionValue)}
                  </TableCell>
                  <TableCell align="right" className={classes.commissionCell}>
                    {formatCurrency(letter.totalEntryValue ?? (parseFloat(letter.entryValue || 0) + parseFloat(letter.commissionValue || 0)))}
                  </TableCell>
                  <TableCell align="right" className={classes.commissionCell}>
                    {(
                      letter.totalEntryPercentage ?? (
                        parseFloat(letter.credit || 0) > 0
                          ? ((parseFloat(letter.totalEntryValue ?? (parseFloat(letter.entryValue || 0) + parseFloat(letter.commissionValue || 0))) / parseFloat(letter.credit)) * 100)
                          : 0
                      )
                    ).toFixed(2)}%
                  </TableCell>
                </TableRow>
              ))}
              {/* Linha de Totais */}
              <TableRow className={classes.totalRow}>
                <TableCell colSpan={6}><strong>TOTAIS ({filteredLetters.length} cartas)</strong></TableCell>
                <TableCell></TableCell>
                <TableCell align="right"></TableCell>
                <TableCell align="right" className={classes.commissionCell}>
                  {formatCurrency(filteredLetters.reduce((sum, l) => sum + parseFloat(l.commissionValue), 0))}
                </TableCell>
                <TableCell align="right" className={classes.commissionCell}>
                  {formatCurrency(filteredLetters.reduce((sum, l) => sum + parseFloat(l.totalEntryValue ?? (parseFloat(l.entryValue || 0) + parseFloat(l.commissionValue || 0))), 0))}
                </TableCell>
                <TableCell align="right" className={classes.commissionCell}>
                  {(() => {
                    const totalCredit = filteredLetters.reduce((sum, l) => sum + parseFloat(l.credit || 0), 0);
                    const totalEntryWithCommission = filteredLetters.reduce((sum, l) => sum + parseFloat(l.totalEntryValue ?? (parseFloat(l.entryValue || 0) + parseFloat(l.commissionValue || 0))), 0);
                    return totalCredit > 0 ? ((totalEntryWithCommission / totalCredit) * 100).toFixed(2) + "%" : "0.00%";
                  })()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Upload PDF */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar Cartas Contempladas (PDF)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Faça upload do PDF mensal da Embracon com as cartas contempladas.
                O sistema irá extrair automaticamente os dados da tabela.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <div
                className={classes.uploadArea}
                onClick={() => document.getElementById("pdf-upload-input").click()}
              >
                <UploadIcon style={{ fontSize: 40, color: "#999" }} />
                <Typography variant="body1" style={{ marginTop: 8 }}>
                  {file ? file.name : "Clique para selecionar o PDF"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Formatos aceitos: PDF
                </Typography>
              </div>
              <input
                id="pdf-upload-input"
                type="file"
                accept=".pdf"
                hidden
                onChange={(e) => setFile(e.target.files[0])}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Porcentagem de Comissão (%)"
                type="number"
                variant="outlined"
                value={commissionPercentage}
                onChange={(e) => setCommissionPercentage(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText="Ex: 3 = 3% sobre o valor de ENTRADA"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={uploading || !file}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? "Importando..." : "Importar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Alterar Comissão */}
      <Dialog open={commissionOpen} onClose={() => setCommissionOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Alterar Comissão</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Altere a porcentagem de comissão para todas as cartas contempladas.
          </Typography>
          <TextField
            fullWidth
            label="Nova Porcentagem (%)"
            type="number"
            variant="outlined"
            value={newCommission}
            onChange={(e) => setNewCommission(e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleUpdateCommission}>
            Atualizar Comissões
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContemplatedLetters;
